import { useEffect, useState, type FormEvent } from 'react';
import { formatManilaDate } from '@dispatchdash/shared';
import {
  fleetCostsApi,
  openStoredImage,
  tripsApi,
  vehiclesApi,
  type CostRollupGroupBy,
  type FleetCostEntry,
  type OwnedVsSubconResult,
  type Trip,
  type Vehicle,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Feedback';
import { formatDate, formatPeso, humanize } from '@/lib/format';
import { adminNavItems } from '@/pages/navItems';

const COST_CLASS_LABELS: Record<FleetCostEntry['class'], string> = {
  vehicle_period: 'Vehicle period (fuel, maintenance)',
  trip_direct: 'Trip direct',
  overhead: 'Overhead',
};

/** Trip statuses a cost can sensibly be booked against (excludes cancelled/declined/not-yet-run). */
const COSTABLE_TRIP_STATUSES = ['completed', 'ready_to_bill', 'billed', 'closed', 'executing', 'dispatched', 'on_hold', 'failed'];

function tripLabel(t: Trip) {
  return t.tripNumber || t.reference || 'Trip';
}

function Money({ value }: { value: string | number | null | undefined }) {
  return <span className="data-num money-cell">{formatPeso(value)}</span>;
}

export function AdminCosts() {
  // Bumped after a cost is logged or allocated so rollups and comparisons refetch.
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);
  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header">
          <p className="kicker">Fleet</p>
          <h1>Cost allocation</h1>
          <p className="text-muted">
            Log vehicle/overhead costs, allocate them to trips by distance share, and compare
            owned vs. subcontracted vehicle economics.
          </p>
        </div>

        <CostEntriesSection onChanged={bump} />
        <RollupsSection refreshKey={refreshKey} />
        <OwnedVsSubconSection refreshKey={refreshKey} />
      </div>
    </Layout>
  );
}

function CostEntriesSection({ onChanged }: { onChanged: () => void }) {
  const toast = useToast();
  const { data: costs, loading, error, reload } = useAsync(() => fleetCostsApi.list(), []);
  const [showLog, setShowLog] = useState(false);
  const [allocateTarget, setAllocateTarget] = useState<FleetCostEntry | null>(null);

  return (
    <section className="costs-section">
      <div className="row-between" style={{ marginBottom: 'var(--space-md)' }}>
        <h2>Cost entries</h2>
        <Button type="button" size="sm" onClick={() => setShowLog(true)}>Log cost</Button>
      </div>

      <DataTable
        data={costs ?? []}
        keyField="id"
        loading={loading}
        loadError={error ? `Could not load cost entries: ${error}` : null}
        onRetry={() => void reload()}
        dense
        emptyMessage="No costs logged yet. Log fuel, maintenance, or overhead costs, then allocate them to trips."
        columns={[
          {
            key: 'class',
            header: 'Class',
            value: (r) => COST_CLASS_LABELS[r.class] ?? humanize(r.class),
            render: (r) => COST_CLASS_LABELS[r.class] ?? humanize(r.class),
          },
          { key: 'category', header: 'Category', render: (r) => humanize(r.category) },
          {
            key: 'amount',
            header: 'Amount',
            align: 'right',
            value: (r) => Number(r.amount),
            render: (r) => <Money value={r.amount} />,
          },
          {
            key: 'allocatedTotal',
            header: 'Allocated',
            align: 'right',
            value: (r) => Number(r.allocatedTotal),
            render: (r) => <Money value={r.allocatedTotal} />,
          },
          {
            key: 'isAllocated',
            header: 'Status',
            render: (r) => <StatusBadge status={r.isAllocated ? 'allocated' : 'unallocated'} size="sm" />,
          },
          {
            key: 'loggedAt',
            header: 'Logged',
            value: (r) => r.loggedAt,
            render: (r) => formatDate(r.loggedAt),
          },
          {
            key: 'actions',
            header: '',
            render: (r) => (
              <div className="row-actions">
                {r.receiptKey && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void openStoredImage(r.receiptKey!).catch((err: { message?: string }) =>
                        toast(err?.message ?? 'The receipt could not be opened.', 'error'),
                      )
                    }
                  >
                    View receipt
                  </Button>
                )}
                <Button type="button" size="sm" variant="ghost" onClick={() => setAllocateTarget(r)}>
                  {r.isAllocated ? 'Reallocate' : 'Allocate'}
                </Button>
              </div>
            ),
          },
        ]}
      />

      <LogCostModal
        open={showLog}
        onClose={() => setShowLog(false)}
        onSaved={(amount) => {
          setShowLog(false);
          toast(`Cost of ${formatPeso(amount)} logged — allocate it to trips next`);
          reload();
          onChanged();
        }}
      />

      <AllocateModal
        cost={allocateTarget}
        onClose={() => setAllocateTarget(null)}
        onAllocated={(count) => {
          const target = allocateTarget;
          setAllocateTarget(null);
          toast(`${target ? formatPeso(target.amount) : 'Cost'} allocated across ${count} trip${count === 1 ? '' : 's'}`);
          reload();
          onChanged();
        }}
      />

      <style>{`
        .costs-section {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          margin-bottom: var(--space-xl);
        }
        .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; }
        .data-num {
          font-family: var(--font-display);
          font-weight: 700;
          font-variant-numeric: tabular-nums;
        }
        .money-cell {
          display: block;
          text-align: right;
          white-space: nowrap;
        }
      `}</style>
    </section>
  );
}

function LogCostModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (amount: string) => void;
}) {
  const { data: vehicles } = useAsync(() => vehiclesApi.list(), []);
  const { data: trips } = useAsync(() => tripsApi.list(), []);
  const [form, setForm] = useState({
    class: 'vehicle_period' as 'trip_direct' | 'vehicle_period' | 'overhead',
    category: '',
    vehicleId: '',
    tripId: '',
    amount: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (form.class === 'trip_direct' && !form.tripId) {
      setError('Select a trip for trip-direct costs');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await fleetCostsApi.create({
        class: form.class,
        category: form.category,
        vehicleId: form.vehicleId || undefined,
        tripId: form.class === 'trip_direct' ? form.tripId : undefined,
        amount: form.amount,
        description: form.description || undefined,
      });
      const savedAmount = form.amount;
      setForm({ class: 'vehicle_period', category: '', vehicleId: '', tripId: '', amount: '', description: '' });
      onSaved(savedAmount);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to log cost');
    } finally {
      setSaving(false);
    }
  };

  const tripOptions = (trips ?? []).filter((t: Trip) => COSTABLE_TRIP_STATUSES.includes(t.status));

  return (
    <Modal open={open} onClose={onClose} title="Log cost">
      <form onSubmit={save}>
        <Select
          label="Class"
          value={form.class}
          onChange={(e) => setForm({ ...form, class: e.target.value as typeof form.class, tripId: '' })}
        >
          <option value="vehicle_period">{COST_CLASS_LABELS.vehicle_period}</option>
          <option value="trip_direct">{COST_CLASS_LABELS.trip_direct}</option>
          <option value="overhead">{COST_CLASS_LABELS.overhead}</option>
        </Select>
        <Input
          label="Category"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          placeholder="fuel, toll, maintenance, insurance…"
          required
        />
        {form.class === 'trip_direct' && (
          <Select
            label="Trip"
            value={form.tripId}
            onChange={(e) => setForm({ ...form, tripId: e.target.value })}
            required
          >
            <option value="">Select trip…</option>
            {tripOptions.map((t: Trip) => (
              <option key={t.id} value={t.id}>
                {tripLabel(t)}
                {t.clientName ? ` · ${t.clientName}` : ''}
                {` · ${humanize(t.status)}`}
              </option>
            ))}
          </Select>
        )}
        {form.class !== 'overhead' && (
          <Select
            label="Vehicle (optional)"
            value={form.vehicleId}
            onChange={(e) => setForm({ ...form, vehicleId: e.target.value })}
          >
            <option value="">— None —</option>
            {(vehicles ?? []).map((v: Vehicle) => (
              <option key={v.id} value={v.id}>{v.plateNumber ?? v.plate}</option>
            ))}
          </Select>
        )}
        <Input
          label="Amount (₱)"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0.01"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          required
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Optional note"
        />
        {error && <p className="text-urgent">{error}</p>}
        <Button type="submit" loading={saving}>Log cost</Button>
      </form>
    </Modal>
  );
}

function AllocateModal({
  cost,
  onClose,
  onAllocated,
}: {
  cost: FleetCostEntry | null;
  onClose: () => void;
  onAllocated: (tripCount: number) => void;
}) {
  const { data: trips } = useAsync(() => tripsApi.list(), []);
  const [tripSearch, setTripSearch] = useState('');
  const [selectedTripIds, setSelectedTripIds] = useState<string[]>([]);
  const [basis, setBasis] = useState<'distance' | 'equal_per_trip'>('distance');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preloadMessage, setPreloadMessage] = useState('');

  useEffect(() => {
    setTripSearch('');
    if (!cost) {
      setSelectedTripIds([]);
      setBasis('distance');
      setPreloadMessage('');
      setError('');
      return;
    }
    let cancelled = false;
    setError('');
    if (!cost.isAllocated) {
      setSelectedTripIds([]);
      setBasis('distance');
      setPreloadMessage('');
      return;
    }
    setPreloadMessage('Loading current allocation…');
    fleetCostsApi
      .allocations(cost.id)
      .then((res) => {
        if (cancelled) return;
        setSelectedTripIds(res.tripIds);
        setBasis(res.basis);
        setPreloadMessage(
          res.tripIds.length
            ? `Current allocation: ${res.tripIds.length} trip(s) preselected. Submitting replaces that live run in rollups (audit retains the change).`
            : 'No current trip allocation found — select trips below.',
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setPreloadMessage('');
        setError((err as { message?: string }).message ?? 'Could not load current allocation');
      });
    return () => {
      cancelled = true;
    };
  }, [cost]);

  const toggleTrip = (id: string) => {
    setSelectedTripIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  const allocate = async () => {
    if (!cost) return;
    setSaving(true);
    setError('');
    try {
      await fleetCostsApi.allocate(cost.id, { tripIds: selectedTripIds, basis });
      const count = selectedTripIds.length;
      setSelectedTripIds([]);
      onAllocated(count);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to allocate');
    } finally {
      setSaving(false);
    }
  };

  const isReallocate = Boolean(cost?.isAllocated);

  const query = tripSearch.trim().toLowerCase();
  const pickableTrips = (trips ?? []).filter((t: Trip) => {
    // Always keep already-selected trips visible so a reallocation can be reviewed/unticked.
    const selected = selectedTripIds.includes(t.id);
    if (!selected && !COSTABLE_TRIP_STATUSES.includes(t.status)) return false;
    if (!query) return true;
    return [t.tripNumber, t.reference, t.clientName, t.origin, t.destination]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(query));
  });

  return (
    <Modal
      open={!!cost}
      onClose={onClose}
      title={
        cost
          ? isReallocate
            ? `Reallocate ${formatPeso(cost.amount)} · ${humanize(cost.category)}`
            : `Allocate ${formatPeso(cost.amount)} · ${humanize(cost.category)}`
          : 'Allocate cost'
      }
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="button" loading={saving} disabled={!selectedTripIds.length} onClick={allocate}>
            {isReallocate ? 'Replace allocation' : 'Allocate'} to {selectedTripIds.length} trip(s)
          </Button>
        </>
      }
    >
      {isReallocate && (
        <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: 'var(--space-md)' }}>
          {preloadMessage ||
            'Submitting replaces the current live allocation used in cost rollups. Prior trip checkboxes are preselected from that run.'}
        </p>
      )}
      <Select
        label="Allocation basis"
        value={basis}
        onChange={(e) => setBasis(e.target.value as typeof basis)}
      >
        <option value="distance">Distance share (falls back to equal split if no zone distances)</option>
        <option value="equal_per_trip">Equal per trip</option>
      </Select>
      <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
        Select the trips this cost should be split across. Cancelled and not-yet-dispatched trips are hidden.
      </p>
      {error && <p className="text-urgent">{error}</p>}
      <Input
        id="allocate-trip-search"
        label="Search trips"
        type="search"
        value={tripSearch}
        onChange={(e) => setTripSearch(e.target.value)}
        placeholder="Trip number, reference, client or place"
      />
      <div className="trip-picker">
        {pickableTrips.map((t: Trip) => (
          <label key={t.id} className="trip-pick-row">
            <input
              type="checkbox"
              checked={selectedTripIds.includes(t.id)}
              onChange={() => toggleTrip(t.id)}
            />
            <span>{tripLabel(t)}</span>
            <span className="text-muted">{t.origin ?? '—'} → {t.destination ?? '—'}</span>
            <StatusBadge status={t.status} size="sm" />
          </label>
        ))}
        {!pickableTrips.length && (
          <p className="text-muted" style={{ padding: 'var(--space-sm) var(--space-md)' }}>
            {query ? 'No trips match your search.' : 'No dispatched or completed trips available.'}
          </p>
        )}
      </div>
      <style>{`
        .trip-picker {
          max-height: 320px;
          overflow: auto;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
        }
        .trip-pick-row {
          display: grid;
          grid-template-columns: auto 100px 1fr auto;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-md);
          border-bottom: 1px solid var(--color-border);
          font-size: 0.8125rem;
          cursor: pointer;
        }
        .trip-pick-row:last-child { border-bottom: none; }
        .trip-pick-row:hover { background: var(--color-sand); }
      `}</style>
    </Modal>
  );
}

const GROUP_BY_OPTIONS: Array<{ value: CostRollupGroupBy; label: string }> = [
  { value: 'trip', label: 'Trip' },
  { value: 'lane', label: 'Lane' },
  { value: 'zone', label: 'Zone' },
  { value: 'client', label: 'Client' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'km', label: 'Per km' },
];

const ROLLUP_HEADERS: Record<string, string> = {
  tripNumber: 'Trip',
  totalCost: 'Total cost',
  allocationCount: 'Allocations',
  tripCount: 'Trips',
  originName: 'Origin',
  destinationName: 'Destination',
  zoneCode: 'Zone code',
  zoneName: 'Zone',
  clientName: 'Client',
  plateNumber: 'Vehicle',
  totalKm: 'Total km',
  costPerKm: 'Cost per km',
};
const ROLLUP_MONEY_KEYS = new Set(['totalCost', 'costPerKm']);
/** Internal ids and codes duplicated by a friendlier column. */
const ROLLUP_HIDDEN_KEYS = new Set(['originCode', 'destinationCode']);

function rollupHeader(key: string) {
  return ROLLUP_HEADERS[key] ?? humanize(key.replace(/([A-Z])/g, '_$1'));
}

function RollupsSection({ refreshKey }: { refreshKey: number }) {
  const [groupBy, setGroupBy] = useState<CostRollupGroupBy>('trip');
  const { data, loading, error, reload } = useAsync(() => fleetCostsApi.rollups(groupBy), [groupBy, refreshKey]);

  const rows = data?.rows ?? [];
  const allKeys = rows.length ? Object.keys(rows[0]) : [];
  const columnKeys = allKeys.filter((k) => !/Id$/.test(k) && !ROLLUP_HIDDEN_KEYS.has(k));
  const keyedRows = rows.map((r, i) => ({ ...r, __rowKey: String(r[allKeys[0]] ?? i) }));

  return (
    <section className="costs-section">
      <div className="row-between" style={{ marginBottom: 'var(--space-md)' }}>
        <h2>Cost rollups</h2>
        <Select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as CostRollupGroupBy)}
          style={{ width: 200, marginBottom: 0 }}
        >
          {GROUP_BY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>Group by {o.label}</option>
          ))}
        </Select>
      </div>

      <DataTable
        data={keyedRows}
        keyField="__rowKey"
        loading={loading}
        loadError={error ? `Could not load rollups: ${error}` : null}
        onRetry={() => void reload()}
        dense
        emptyMessage="No allocated costs for this grouping yet. Allocate cost entries above to see them here."
        columns={columnKeys.map((key) => ({
          key,
          header: rollupHeader(key),
          align: ROLLUP_MONEY_KEYS.has(key) ? ('right' as const) : undefined,
          value: (r: Record<string, unknown>) => {
            const v = r[key];
            if (ROLLUP_MONEY_KEYS.has(key)) return v == null ? null : Number(v);
            return typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean' ? v : null;
          },
          render: (r: Record<string, unknown>) => {
            const v = r[key];
            if (ROLLUP_MONEY_KEYS.has(key)) return <Money value={v as string | number | null} />;
            if (key === 'status' && typeof v === 'string') return <StatusBadge status={v} size="sm" />;
            if (key === 'ownership' && typeof v === 'string') return humanize(v);
            if (typeof v === 'number') {
              return <span className="money-cell">{v.toLocaleString('en-PH', { maximumFractionDigits: 2 })}</span>;
            }
            return String(v ?? '—');
          },
        }))}
      />

      <style>{`
        .costs-section {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          margin-bottom: var(--space-xl);
        }
      `}</style>
    </section>
  );
}

function OwnedVsSubconSection({ refreshKey }: { refreshKey: number }) {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [periodStart, setPeriodStart] = useState(formatManilaDate(monthAgo));
  const [periodEnd, setPeriodEnd] = useState(formatManilaDate(today));
  const { data, loading, error } = useAsync<OwnedVsSubconResult>(
    () => fleetCostsApi.ownedVsSubcon(periodStart, periodEnd),
    [periodStart, periodEnd, refreshKey],
  );

  return (
    <section className="costs-section">
      <h2>Owned vs. subcontracted</h2>
      <p className="text-muted" style={{ marginTop: '-0.25rem' }}>
        Average cost per km for owned fleet vs. subcontracted vehicles over a period.
      </p>
      <div className="row" style={{ margin: 'var(--space-md) 0' }}>
        <Input
          label="From"
          type="date"
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
        />
        <Input
          label="To"
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : error ? (
        <p className="text-urgent">{error}</p>
      ) : data ? (
        <div className="compare-grid">
          <CompareCard label="Owned" bucket={data.owned} highlight={data.cheaperPerKm === 'owned'} />
          <CompareCard label="Subcontracted" bucket={data.subcon} highlight={data.cheaperPerKm === 'subcon'} />
        </div>
      ) : null}

      <style>{`
        .costs-section {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          margin-bottom: var(--space-xl);
        }
        .row { display: flex; gap: var(--space-md); flex-wrap: wrap; }
        .compare-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-md);
        }
        @media (max-width: 700px) {
          .compare-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
}

function CompareCard({
  label,
  bucket,
  highlight,
}: {
  label: string;
  bucket: OwnedVsSubconResult['owned'];
  highlight: boolean;
}) {
  return (
    <div className={`compare-card ${highlight ? 'highlight' : ''}`}>
      <h3>{label} {highlight && <span className="badge-cheaper">Cheaper per km</span>}</h3>
      <dl>
        <dt>Total cost</dt><dd>{formatPeso(bucket.totalCost)}</dd>
        <dt>Total km</dt><dd>{bucket.totalKm.toLocaleString('en-PH')}</dd>
        <dt>Trips</dt><dd>{bucket.tripCount}</dd>
        <dt>Avg cost per km</dt><dd>{formatPeso(bucket.avgCostPerKm)}</dd>
        <dt>Avg cost per trip</dt><dd>{formatPeso(bucket.avgCostPerTrip)}</dd>
      </dl>
      <style>{`
        .compare-card {
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .compare-card.highlight {
          border-color: var(--color-signal);
          background: var(--color-sand);
        }
        .compare-card h3 {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          margin-bottom: var(--space-md);
        }
        .badge-cheaper {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          background: var(--color-signal);
          color: var(--color-ink);
          padding: 2px 8px;
          border-radius: var(--radius-sm);
        }
        .compare-card dl {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: var(--space-xs) var(--space-md);
          font-size: 0.9375rem;
        }
        .compare-card dt { color: var(--color-text-muted); }
        .compare-card dd { font-weight: 600; text-align: right; }
      `}</style>
    </div>
  );
}
